import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import routeManifest from "@/data/route-manifest.json";
import { PageViewAnalyticsService } from "@/services/analytics/PageViewAnalyticsService";
import { PageViewsPeriod, PageViewsTab, PageViewsTabId, RouteManifestEntry } from "@/types/admin/page-views";
import AnalysisTab from "./AnalysisTab";
import { DEFAULT_TAB, PAGE_VIEWS_TABS, PERIOD_OPTIONS } from "./constants";
import TrafficTab from "./TrafficTab";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const isTabId = (value: string | null): value is PageViewsTabId => PAGE_VIEWS_TABS.some((t) => t.id === value);

const PageViewAnalyticsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<PageViewsPeriod>("30d");
  const rawTab = searchParams.get("tab");
  const activeTabId: PageViewsTabId = isTabId(rawTab) ? rawTab : DEFAULT_TAB;

  const analyzeMutation = useMutation({
    mutationFn: () =>
      PageViewAnalyticsService.analyzeUnusedPages({ period }, routeManifest.routes as RouteManifestEntry[]),
    onError: (err: Error) => toast.error(err.message ?? "Analysis failed"),
  });

  const handleSelectTab = (id: PageViewsTabId) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_TAB) next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const handlePeriodChange = (value: PageViewsPeriod) => {
    setPeriod(value);
    analyzeMutation.reset();
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
      <div className="container mx-auto max-w-[1920px] w-full px-4 py-5 md:py-6">
        {/*
          WorkspaceSwitcher is fixed at `right-6 top-5` (md and up) and owns the
          top-right corner, so the period picker has to stop short of it.
        */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between md:pr-[104px]">
          <PageViewsTabBar activeTabId={activeTabId} onSelect={handleSelectTab} />
          <Select value={period} onValueChange={(v) => handlePeriodChange(v as PageViewsPeriod)}>
            <SelectTrigger className="h-10 w-full shrink-0 rounded-xl border-[#E5EAF0] bg-white text-base font-medium text-gray-700 sm:w-44 sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {activeTabId === "traffic" ? (
          <TrafficTab period={period} />
        ) : (
          <AnalysisTab
            result={analyzeMutation.data}
            isPending={analyzeMutation.isPending}
            onRun={() => analyzeMutation.mutate()}
          />
        )}
      </div>
    </div>
  );
};

const PageViewsTabBar = ({ activeTabId, onSelect }: { activeTabId: PageViewsTabId; onSelect: (id: PageViewsTabId) => void }) => {
  // The smart analysis is the page's standout capability, so it renders as the
  // gradient feature pill — same treatment as the Views tab in the Inbox.
  const renderAnalysisTab = (tab: PageViewsTab, isActive: boolean) => (
    <button
      key={tab.id}
      onClick={() => onSelect(tab.id)}
      className={`group flex min-h-[40px] items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-200 hover:scale-[1.03] hover:shadow-xl hover:shadow-genie-primary/40 ${
        isActive
          ? "shadow-lg shadow-genie-primary/40 ring-2 ring-[#4E9CFF]/50 ring-offset-2 ring-offset-[#F9FAFC]"
          : "shadow-md shadow-genie-primary/30"
      }`}
    >
      <GenieIcon
        name={tab.genieIcon}
        size={16}
        color="#ffffff"
        label=""
        className="shrink-0 transition-transform duration-200 group-hover:scale-110"
      />
      <span>{tab.label}</span>
    </button>
  );

  const renderTab = (tab: PageViewsTab) => {
    const isActive = tab.id === activeTabId;

    if (tab.id === "analysis") {
      return renderAnalysisTab(tab, isActive);
    }

    if (isActive) {
      return (
        <button
          key={tab.id}
          onClick={() => onSelect(tab.id)}
          className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md shadow-genie-primary/20"
        >
          <span className="flex items-center gap-2 px-3.5 py-2 rounded-[10px] bg-white text-sm font-semibold text-[#222B45]">
            <GenieIcon name={tab.genieIcon} size={16} className="h-4 w-4" />
            {tab.label}
          </span>
        </button>
      );
    }

    return (
      <button
        key={tab.id}
        onClick={() => onSelect(tab.id)}
        className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border bg-white border-[#E5EAF0] text-gray-700 hover:border-genie-primary/40 hover:text-genie-primary transition-all"
      >
        <GenieIcon name={tab.genieIcon} size={16} className="h-4 w-4" />
        {tab.label}
      </button>
    );
  };

  return <div className="flex flex-wrap items-center gap-2">{PAGE_VIEWS_TABS.map(renderTab)}</div>;
};

export default PageViewAnalyticsPage;
